import { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OfferCenter = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // New project form
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    client_name: '',
    tender_reference: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/offers/projects`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const createProject = async () => {
    if (!newProject.name) {
      alert('Please enter a project name');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newProject.name);
      if (newProject.description) formData.append('description', newProject.description);
      if (newProject.client_name) formData.append('client_name', newProject.client_name);
      if (newProject.tender_reference) formData.append('tender_reference', newProject.tender_reference);

      const response = await axios.post(`${API}/offers/projects`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setProjects([response.data, ...projects]);
      setShowNewProjectDialog(false);
      setNewProject({ name: '', description: '', client_name: '', tender_reference: '' });
      setSelectedProject(response.data);
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const loadProject = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/offers/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedProject(response.data);
    } catch (error) {
      console.error('Failed to load project:', error);
      alert('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (projectId, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);

    try {
      const response = await axios.post(
        `${API}/offers/projects/${projectId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Reload project to get updated documents
      await loadProject(projectId);
      return response.data;
    } catch (error) {
      console.error('Failed to upload document:', error);
      throw error;
    }
  };

  const deleteDocument = async (documentId, docType) => {
    if (!confirm(`Are you sure you want to delete this ${docType} document?${docType === 'tender' ? ' This will also clear the analysis results.' : ''}`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/offers/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      // Reload project to get updated documents
      if (selectedProject) {
        await loadProject(selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      alert(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const analyzeTender = async (projectId) => {
    setAnalyzing(true);
    try {
      // Start the analysis (returns immediately)
      await axios.post(
        `${API}/offers/projects/${projectId}/analyze-tender`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      // Poll for status until complete
      let attempts = 0;
      const maxAttempts = 90; // 3 minutes max (analysis can take a while)
      let lastError = null;
      
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/offers/projects/${projectId}/analysis-status`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          const status = statusResponse.data.status;
          const progressMessage = statusResponse.data.error; // We use error field for progress messages during analysis
          
          if (status === 'analyzed') {
            // Analysis complete - reload project
            await loadProject(projectId);
            setAnalyzing(false);
            alert(`Tender analyzed successfully! Found ${statusResponse.data.requirements_count} requirements.`);
            return;
          } else if (status === 'error') {
            setAnalyzing(false);
            alert(`Analysis failed: ${statusResponse.data.error || 'Unknown error'}`);
            return;
          } else if (attempts < maxAttempts) {
            attempts++;
            // Log progress to console
            if (progressMessage && progressMessage !== lastError) {
              console.log(`Analysis progress: ${progressMessage}`);
              lastError = progressMessage;
            }
            setTimeout(pollStatus, 2000); // Poll every 2 seconds
          } else {
            setAnalyzing(false);
            // Check one more time if it actually completed
            await loadProject(projectId);
            alert('Analysis is taking longer than expected. The page has been refreshed - check if analysis completed.');
          }
        } catch (pollError) {
          console.error('Status poll error:', pollError);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setAnalyzing(false);
            await loadProject(projectId);
          }
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollStatus, 3000);
      
    } catch (error) {
      console.error('Failed to start tender analysis:', error);
      alert(error.response?.data?.detail || 'Failed to start analysis');
      setAnalyzing(false);
    }
  };

  const generateOffer = async (projectId) => {
    setGenerating(true);
    try {
      // Start the generation (returns immediately)
      await axios.post(
        `${API}/offers/projects/${projectId}/generate-offer`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      // Poll for status until complete
      let attempts = 0;
      const maxAttempts = 90; // 3 minutes max
      
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/offers/projects/${projectId}/generation-status`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          const status = statusResponse.data.status;
          
          if (status === 'generated') {
            // Generation complete - reload project
            await loadProject(projectId);
            setGenerating(false);
            alert(`Offer generated successfully! Compliance matrix has ${statusResponse.data.compliance_count} entries.`);
            return;
          } else if (status === 'error') {
            setGenerating(false);
            alert(`Generation failed: ${statusResponse.data.error || 'Unknown error'}`);
            return;
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000); // Poll every 2 seconds
          } else {
            setGenerating(false);
            alert('Generation is taking longer than expected. Please refresh the page to check status.');
          }
        } catch (pollError) {
          console.error('Status poll error:', pollError);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setGenerating(false);
            await loadProject(projectId);
          }
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollStatus, 3000);
      
    } catch (error) {
      console.error('Failed to start offer generation:', error);
      alert(error.response?.data?.detail || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const deleteProject = async (projectId) => {
    if (!confirm('Delete this project? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/offers/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setProjects(projects.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project');
    }
  };

  const renderProjectsList = () => (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Offer Projects</CardTitle>
            <CardDescription>Manage your tender responses and proposals</CardDescription>
          </div>
          <Button onClick={() => setShowNewProjectDialog(true)}>
            + New Project
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {projects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No projects yet. Create your first offer project!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((project) => (
              <div
                key={project.id}
                className="border rounded-lg p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => loadProject(project.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{project.name}</h3>
                    {project.client_name && (
                      <p className="text-sm text-muted-foreground">Client: {project.client_name}</p>
                    )}
                    {project.tender_reference && (
                      <p className="text-sm text-muted-foreground">Ref: {project.tender_reference}</p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Badge variant={
                        project.status === 'generated' ? 'default' :
                        project.status === 'analyzing' ? 'secondary' : 'outline'
                      }>
                        {project.status}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteProject(project.id);
                    }}
                  >
                    🗑️
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderProjectDetails = () => {
    if (!selectedProject) return null;

    return (
      <div className="space-y-6">
        {/* Project Header */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{selectedProject.name}</CardTitle>
                <CardDescription>
                  {selectedProject.client_name && `Client: ${selectedProject.client_name}`}
                  {selectedProject.tender_reference && ` • Ref: ${selectedProject.tender_reference}`}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant={
                  selectedProject.status === 'generated' ? 'default' :
                  selectedProject.status === 'analyzing' ? 'secondary' : 'outline'
                }>
                  {selectedProject.status}
                </Badge>
                <Button variant="outline" size="sm" onClick={() => setSelectedProject(null)}>
                  ← Back
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Document Upload Sections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Tender Document */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">📄 Tender Document</CardTitle>
              <CardDescription>Upload the tender/RFP</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedProject.tender_document ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span className="text-sm truncate flex-1">{selectedProject.tender_document.name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                      onClick={() => deleteDocument(selectedProject.tender_document.id, 'tender')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </Button>
                  </div>
                  {selectedProject.tender_analysis && (
                    <Badge variant="default">Analyzed</Badge>
                  )}
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        uploadDocument(selectedProject.id, e.target.files[0], 'tender')
                          .catch(err => alert('Failed to upload'));
                      }
                    }}
                    className="text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    PDF, DOC, XLS, PPT
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Knowledge Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">📚 Knowledge Base</CardTitle>
              <CardDescription>Brochures, datasheets, specs</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedProject.knowledge_documents?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {selectedProject.knowledge_documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between text-xs p-1 bg-slate-50 rounded">
                      <span className="truncate flex-1">{doc.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 ml-1"
                        onClick={() => deleteDocument(doc.id, 'knowledge')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    uploadDocument(selectedProject.id, e.target.files[0], 'knowledge')
                      .catch(err => alert('Failed to upload'));
                  }
                }}
                className="text-sm"
              />
            </CardContent>
          </Card>

          {/* Example Offers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">📋 Example Offers</CardTitle>
              <CardDescription>Templates to follow</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedProject.example_documents?.length > 0 && (
                <div className="space-y-1 mb-3">
                  {selectedProject.example_documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between text-xs p-1 bg-slate-50 rounded">
                      <span className="truncate flex-1">{doc.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 ml-1"
                        onClick={() => deleteDocument(doc.id, 'example')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    uploadDocument(selectedProject.id, e.target.files[0], 'example')
                      .catch(err => alert('Failed to upload'));
                  }
                }}
                className="text-sm"
              />
            </CardContent>
          </Card>
        </div>

        {/* Analysis Section */}
        {selectedProject.tender_document && (
          <Card>
            <CardHeader>
              <CardTitle>🔍 Tender Analysis</CardTitle>
              <CardDescription>AI-powered requirements extraction</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedProject.tender_analysis ? (
                <div className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded max-h-96 overflow-y-auto">
                      {selectedProject.tender_analysis}
                    </div>
                  </div>
                  <Button
                    onClick={() => analyzeTender(selectedProject.id)}
                    disabled={analyzing}
                    variant="outline"
                    size="sm"
                  >
                    {analyzing ? 'Re-analyzing...' : '🔄 Re-analyze'}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => analyzeTender(selectedProject.id)}
                  disabled={analyzing}
                >
                  {analyzing ? 'Analyzing...' : '🔍 Analyze Tender'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Generate Offer Section */}
        {selectedProject.tender_analysis && (
          <Card>
            <CardHeader>
              <CardTitle>✍️ Generate Offer</CardTitle>
              <CardDescription>AI-generated proposal based on all inputs</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Custom Instructions */}
              <div className="space-y-3 mb-6">
                <Label htmlFor="custom-instructions">
                  Custom Instructions for AI (Optional)
                </Label>
                <Textarea
                  id="custom-instructions"
                  placeholder="Add any specific instructions for how the offer should be written. For example: 'Focus on sustainability', 'Emphasize quick delivery times', 'Use formal tone', etc."
                  value={selectedProject.custom_instructions || ''}
                  onChange={(e) => {
                    // Update local state immediately for responsive typing
                    setSelectedProject({
                      ...selectedProject,
                      custom_instructions: e.target.value
                    });
                  }}
                  onBlur={async (e) => {
                    // Save to backend when user clicks away
                    try {
                      const formData = new FormData();
                      formData.append('custom_instructions', e.target.value);
                      
                      await axios.put(
                        `${API}/offers/projects/${selectedProject.id}`,
                        formData,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                      );
                    } catch (error) {
                      console.error('Failed to save instructions:', error);
                      alert('Failed to save custom instructions');
                    }
                  }}
                  rows={3}
                  className="resize-vertical"
                />
                <p className="text-xs text-muted-foreground">
                  These instructions will be used by AI when generating or regenerating the offer. Changes save automatically when you click outside.
                </p>
              </div>
              
              {selectedProject.generated_offer_text ? (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => copyToClipboard(selectedProject.generated_offer_text)}
                      variant="outline"
                      size="sm"
                    >
                      📋 Copy Offer Text
                    </Button>
                    <Button
                      onClick={() => generateOffer(selectedProject.id)}
                      disabled={generating}
                      variant="outline"
                      size="sm"
                    >
                      {generating ? 'Regenerating...' : '🔄 Regenerate'}
                    </Button>
                  </div>
                  
                  {/* Editable Offer Text */}
                  <div className="space-y-2">
                    <Label htmlFor="offer-text">Generated Offer Text (Editable)</Label>
                    <Textarea
                      id="offer-text"
                      value={selectedProject.generated_offer_text}
                      onChange={(e) => {
                        setSelectedProject({
                          ...selectedProject,
                          generated_offer_text: e.target.value
                        });
                      }}
                      onBlur={async (e) => {
                        try {
                          const formData = new FormData();
                          formData.append('generated_offer_text', e.target.value);
                          
                          await axios.put(
                            `${API}/offers/projects/${selectedProject.id}`,
                            formData,
                            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                          );
                        } catch (error) {
                          console.error('Failed to save edits:', error);
                          alert('Failed to save changes');
                        }
                      }}
                      rows={20}
                      className="font-mono text-sm resize-vertical border p-3"
                    />
                    <p className="text-xs text-muted-foreground">
                      Edit the text directly. Changes are saved automatically when you click outside the text area.
                    </p>
                  </div>

                  {/* Compliance Matrix */}
                  {selectedProject.compliance_matrix?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold mb-3">📊 Compliance Matrix ({selectedProject.compliance_matrix?.length || 0} items)</h3>
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm border">
                          <thead className="bg-slate-100 sticky top-0">
                            <tr>
                              <th className="border p-2 text-left w-24">Req ID</th>
                              <th className="border p-2 text-left w-64">Requirement Summary</th>
                              <th className="border p-2 text-left w-28">Status</th>
                              <th className="border p-2 text-left w-48">Offer Section</th>
                              <th className="border p-2 text-left">How We Comply</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedProject.compliance_matrix.map((item, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="border p-2 font-mono text-sm">{item.requirement_id}</td>
                                <td className="border p-2 text-sm">{item.requirement_summary || item.requirement || '-'}</td>
                                <td className="border p-2">
                                  <Badge variant={
                                    (item.compliance_status || item.status) === 'Compliant' ? 'default' :
                                    (item.compliance_status || item.status) === 'Partially Compliant' ? 'secondary' : 'destructive'
                                  }>
                                    {item.compliance_status || item.status || 'Unknown'}
                                  </Badge>
                                </td>
                                <td className="border p-2 text-sm font-mono">{item.offer_section || item.reference || '-'}</td>
                                <td className="border p-2 text-sm">{item.compliance_explanation || item.comments || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <Button
                        onClick={() => {
                          // Generate matrix text for copying
                          const matrixText = selectedProject.compliance_matrix.map(
                            item => `${item.requirement_id}\t${item.requirement_summary || item.requirement || ''}\t${item.compliance_status || item.status || ''}\t${item.offer_section || item.reference || ''}\t${item.compliance_explanation || item.comments || ''}`
                          ).join('\n');
                          copyToClipboard(`Requirement ID\tRequirement\tStatus\tReference\tExplanation\n${matrixText}`);
                        }}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        📋 Copy Matrix
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => generateOffer(selectedProject.id)}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : '✨ Generate Offer'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {!selectedProject ? renderProjectsList() : renderProjectDetails()}

      {/* New Project Dialog */}
      <Dialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Offer Project</DialogTitle>
            <DialogDescription>
              Start a new tender response or proposal project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Dubai Airport Lighting Tender 2025"
              />
            </div>
            <div>
              <Label htmlFor="client-name">Client Name</Label>
              <Input
                id="client-name"
                value={newProject.client_name}
                onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                placeholder="e.g., Dubai International Airport"
              />
            </div>
            <div>
              <Label htmlFor="tender-ref">Tender Reference</Label>
              <Input
                id="tender-ref"
                value={newProject.tender_reference}
                onChange={(e) => setNewProject({ ...newProject, tender_reference: e.target.value })}
                placeholder="e.g., OMDB-2025-AGL-001"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description of the project..."
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowNewProjectDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createProject} disabled={loading}>
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
